/**
 * The service layer — LV §161 names these by name:
 *
 * > `siteService`, `assetService`, `workOrderService`, `maintenanceService`,
 * > `inspectionService`, `inventoryService`, `documentService`
 *
 * Every one is a thin, typed wrapper over `http`. They contain no decisions — a
 * service that decided whether a transition were allowed, or computed a KPI, would be
 * business logic in the frontend (LV §223). What they do contain is the URL, the
 * shape, and nothing else.
 *
 * Two consequences visible throughout:
 *
 * - A status change calls an **action endpoint**, never `PATCH status` (LV §110).
 * - Every update sends `version`, because the server refuses a stale write with `409`
 *   and the client has to have something to send (LV §105).
 */

import { http } from "@/services/http";
import type {
  Asset,
  AssetCategoryDefinition,
  AssetHistoryEntry,
  AuditEntry,
  BookingResponse,
  Company,
  ComplianceRequirement,
  ConfigSetting,
  Contractor,
  ContractorQualification,
  ContractorQualificationCheck,
  ContractorQualificationExpiry,
  CriticalityAssessment,
  CriticalityModel,
  Dashboard,
  DataQualityReport,
  DataQualityViolations,
  DateOnly,
  DocumentApproval,
  DocumentMeta,
  EnumCatalogue,
  ExportJob,
  Failure,
  FailureReportResponse,
  FeatureFlags,
  Finding,
  GateStatus,
  GrantableRole,
  HazardAssessment,
  Inspection,
  InspectionCompletion,
  InspectionDetail,
  InspectionTemplate,
  Kpi,
  LowStockRow,
  MaintenanceCall,
  ManageableSite,
  MaintenanceIntervalChange,
  MaintenancePlan,
  MaintenancePreviewReport,
  Material,
  MaterialListItem,
  MaterialAssetRelation,
  Me,
  Measurement,
  MeasuringInstrument,
  MeterReading,
  MeterWithReading,
  Notification,
  OpenWorkOrderRef,
  Operation,
  Page,
  PpeType,
  PriorityDefinition,
  QualificationDefinition,
  RcmAssessment,
  Region,
  ReorderRequest,
  ReorderRequestStatus,
  RepeatAssessment,
  RetentionPolicyDefinition,
  Revision,
  RevisionStatusReport,
  RoleDefinition,
  SafetyBriefing,
  SearchResponse,
  SimulationResponse,
  Site,
  SiteGrantDetail,
  SiteSummary,
  Standard,
  StockBlockResponse,
  StockLocation,
  StockLocationAdmin,
  StockPolicy,
  StockStatus,
  StockTransaction,
  StrategyRecommendation,
  Team,
  TimeEntry,
  User,
  UserDeactivation,
  UserDetail,
  Uuid,
  WorkOrder,
  WorkOrderAction,
  WorkOrderDetail,
  WorkOrderMaterial,
  WorkOrderPpe,
  WorkOrderSafetyState,
  WorkPermit,
  WorkPlan,
  WorkPlanStep,
  WorkPlanStepMaterial,
} from "@/types/api";

/** Paging and filtering are server-side throughout (LV §121-122). */
export interface ListParams {
  page?: number;
  page_size?: number;
  search?: string;
}

// ---------------------------------------------------------------- identity
// ---------------------------------------------------------------- identity
export const identityService = {
  me: () => http.get<Me>("/me"),

  users: (
    params: ListParams & { active?: boolean; site_id?: Uuid; role_code?: string } = {},
  ) => http.get<Page<User>>("/users", params),

  user: (userId: Uuid) => http.get<UserDetail>(`/users/${userId}`),

  createUser: (body: {
    display_name: string;
    email: string;
    employee_number?: string;
    external_auth_id?: string;
    default_site_id?: Uuid;
    language?: string;
  }) => http.post<User>("/users", body),

  /**
   * Master data only. There is no `active` here — switching an account off is an
   * action endpoint with three guards behind it (LV §110). A reason is required when
   * `email` or `external_auth_id` changes: those decide who can sign in as this
   * person, and the server refuses without one (422 CHANGE_REASON_REQUIRED).
   */
  updateUser: (
    userId: Uuid,
    body: Record<string, unknown> & { version: number; reason?: string },
  ) => http.patch<User>(`/users/${userId}`, body),

  /**
   * The kill switch. Rejects once with `422 OPEN_WORK_ASSIGNED` when the account still
   * carries open orders — `error.details.open_work_orders` holds the order numbers.
   * Show them, then repeat with `acknowledgeOpenWork: true`. Do not send `true` on the
   * first call: the point of the refusal is that a person saw the list.
   */
  deactivateUser: (
    userId: Uuid,
    reason: string,
    acknowledgeOpenWork = false,
    version?: number,
  ) =>
    http.post<UserDeactivation>(`/users/${userId}/deactivate`, {
      reason,
      acknowledge_open_work: acknowledgeOpenWork,
      version,
    }),

  activateUser: (userId: Uuid, reason?: string, version?: number) =>
    http.post<UserDeactivation>(`/users/${userId}/activate`, { reason, version }),

  deleteUser: (userId: Uuid, reason: string) =>
    http.delete<void>(`/users/${userId}`, { reason }),

  restoreUser: (userId: Uuid, reason: string) =>
    http.post<User>(`/users/${userId}/restore`, { reason }),

  userQualifications: (userId: Uuid) =>
    http.get<
      {
        id: Uuid;
        qualification_code: string;
        qualification_name: string;
        valid_from: DateOnly | null;
        valid_until: DateOnly | null;
        status: string;
      }[]
    >(`/users/${userId}/qualifications`),

  userOpenWork: (userId: Uuid) =>
    http.get<OpenWorkOrderRef[]>(`/users/${userId}/open-work`),
};

// ---------------------------------------------------------------- site grants (LV §21)
export const siteGrantService = {
  /**
   * The access list. `site_id` answers "wer hat Zugriff auf Putlitz", `user_id`
   * answers "was hat diese Person". Withdrawn grants are included unless
   * `active_only` is set — they are the record of access somebody once had (LV §11).
   */
  list: (
    params: ListParams & {
      site_id?: Uuid;
      user_id?: Uuid;
      role_code?: string;
      active_only?: boolean;
    } = {},
  ) => http.get<Page<SiteGrantDetail>>("/site-grants", params),

  forUser: (userId: Uuid) =>
    http.get<SiteGrantDetail[]>(`/users/${userId}/site-grants`),

  /**
   * Which roles may be handed out at this site, decided by the server. Call it before
   * rendering the role picker; never derive the answer from `me().permissions`, which
   * would be the client taking an authorisation decision (LV §134).
   */
  grantableRoles: (siteId: Uuid) =>
    http.get<GrantableRole[]>("/users/grantable-roles", { site_id: siteId }),

  grant: (
    userId: Uuid,
    body: {
      site_id: Uuid;
      role_code: string;
      valid_from?: DateOnly;
      valid_until?: DateOnly;
      reason?: string;
    },
  ) => http.post<SiteGrantDetail>(`/users/${userId}/site-grants`, body),

  /**
   * Take access away. There is no delete — this sets the last day of validity, which
   * defaults to today (LV §11, §21). A reason is mandatory. For the immediate,
   * group-wide cut use `identityService.deactivateUser`.
   */
  withdraw: (
    userId: Uuid,
    grantId: Uuid,
    reason: string,
    effectiveDate?: DateOnly,
    version?: number,
  ) =>
    http.post<SiteGrantDetail>(`/users/${userId}/site-grants/${grantId}/withdraw`, {
      reason,
      effective_date: effectiveDate,
      version,
    }),

  /** Lengthens only. `validUntil: null` lifts the end date. Shortening → `withdraw`. */
  extend: (
    userId: Uuid,
    grantId: Uuid,
    validUntil: DateOnly | null,
    reason?: string,
    version?: number,
  ) =>
    http.post<SiteGrantDetail>(`/users/${userId}/site-grants/${grantId}/extend`, {
      valid_until: validUntil,
      reason,
      version,
    }),
};

// ---------------------------------------------------------------- companies (LV §12)
export const companyService = {
  /**
   * Every company — there are two. Needs `company.manage`, which is why a screen asks
   * for it only when the user holds that permission: everybody else is answered `403`.
   */
  list: () => http.get<Company[]>("/companies"),
};

// ---------------------------------------------------------------- regions (LV §9)
export const regionService = {
  /**
   * Every Regionalbereich, by name — unpaginated master data, like `/companies` (#413).
   *
   * `include_deleted` is the administration list's and needs `region.manage`; the server
   * refuses it with `403` rather than quietly answering with the live rows.
   * `with_readable_sites` is the site list filter's: only regions holding at least one
   * live plant the caller may read, so no option can only ever answer with nothing.
   */
  list: (
    params: {
      company_id?: Uuid;
      include_deleted?: boolean;
      with_readable_sites?: boolean;
    } = {},
  ) => http.get<Region[]>("/regions", params),

  get: (id: Uuid) => http.get<Region>(`/regions/${id}`),

  /** `{"Standorte": n}`, deleted plants included — they block the deletion too. */
  usage: (id: Uuid) =>
    http.get<{ usages: Record<string, number> }>(`/regions/${id}/usage`),

  /** `code` is upper-cased by the server; `company_id` is fixed from here on. */
  create: (body: {
    company_id: Uuid;
    code: string;
    name: string;
    description?: string | null;
  }) => http.post<Region>("/regions", body),

  /**
   * Name and description only. `code` and `company_id` are refused as unknown fields, and
   * `version` is required — a stale one answers `409` without overwriting (LV §105).
   */
  update: (
    id: Uuid,
    body: {
      version: number;
      name?: string;
      description?: string | null;
      reason?: string;
    },
  ) => http.patch<Region>(`/regions/${id}`, body),

  /**
   * Soft delete (LV §11). Refused with `REGION_IN_USE` while any plant points at it. The
   * reason is mandatory, three characters at least (`DeleteWithReasonRequest`, #316).
   */
  remove: (id: Uuid, reason: string) => http.delete<void>(`/regions/${id}`, { reason }),

  /** The reason is mandatory here, as on every restore (LV §11, §102). */
  restore: (id: Uuid, reason: string) =>
    http.post<Region>(`/regions/${id}/restore`, { reason }),
};

// ---------------------------------------------------------------- sites (LV §108)
export const siteService = {
  /**
   * `region_id` narrows to one Regionalbereich and never widens the caller's scope; an
   * unknown or deleted one is `422 REGION_REFERENCE_UNKNOWN`, never an empty page.
   * `without_region` lists the plants not assigned yet (LV §9, #413).
   */
  list: (
    params: ListParams & {
      status?: string;
      region_id?: Uuid;
      without_region?: boolean;
    } = {},
  ) => http.get<Page<Site>>("/sites", params),

  get: (id: Uuid) => http.get<Site>(`/sites/${id}`),

  summary: (id: Uuid) => http.get<SiteSummary>(`/sites/${id}/summary`),

  create: (body: Record<string, unknown>) => http.post<Site>("/sites", body),

  update: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<Site>(`/sites/${id}`, body),

  /**
   * `DELETE` means soft delete (LV §11, §108). The reason is mandatory, three characters
   * at least (`DeleteWithReasonRequest`, #316), so the body is never left out.
   */
  deactivate: (id: Uuid, reason: string) =>
    http.delete<void>(`/sites/${id}`, { reason }),

  /**
   * The site lifecycle (LV §13). A status is never sent in `update` — it moves
   * through these named actions, and which ones are legal from the current status
   * is the backend's decision, not this file's (LV §110, §134).
   */
  changeStatus: (
    id: Uuid,
    action: SiteAction,
    body: { reason?: string; version?: number } = {},
  ) => http.post<Site>(`/sites/${id}/${action}`, body),
};

// ---------------------------------------------------------------- teams (LV §22, #483)
export const teamService = {
  /**
   * The teams the caller may plan with: those at a plant they may read, and the
   * group-wide ones. `site_id` narrows to that plant's teams plus the group-wide ones and
   * never widens the caller's scope — a plant outside it answers an empty page (LV §9).
   * Inactive teams only with `include_inactive`; sorted by name, then code.
   */
  list: (
    params: ListParams & {
      site_id?: Uuid;
      include_inactive?: boolean;
    } = {},
  ) => http.get<Page<Team>>("/teams", params),

  get: (id: Uuid) => http.get<Team>(`/teams/${id}`),
};

/** The seven edges of `domain/organisation/site_lifecycle.py`. */
export type SiteAction =
  | "start-construction"
  | "start-commissioning"
  | "commission"
  | "partial-shutdown"
  | "shutdown"
  | "resume-operation"
  | "decommission";

/** The ten edges of `domain/assets/lifecycle.py`. */
export type AssetAction =
  | "install"
  | "commission"
  | "set-standby"
  | "activate"
  | "report-defect"
  | "return-to-service"
  | "take-out-of-service"
  | "remove"
  | "reinstall"
  | "scrap";

/** The five edges of `domain/compliance/standard_lifecycle.py`. */
export type StandardAction =
  "require-review" | "confirm-review" | "supersede" | "mark-not-applicable" | "reopen";

/** The five edges of `domain/compliance/requirement_lifecycle.py`. */
export type RequirementAction =
  "start" | "implement" | "request-review" | "mark-not-applicable" | "reopen";

// ---------------------------------------------------------------- assets (LV §109)
export const assetService = {
  list: (
    params: ListParams & {
      site_id?: Uuid;
      /** Narrows within the caller's scope, never widens it (LV §9, #413). */
      region_id?: Uuid;
      parent_asset_id?: Uuid;
      status?: string;
      criticality_class?: string;
      category_id?: Uuid;
      safety_relevant?: boolean;
      top_level_only?: boolean;
    } = {},
  ) => http.get<Page<Asset>>("/assets", params),

  get: (id: Uuid) => http.get<Asset>(`/assets/${id}`),

  children: (id: Uuid) => http.get<Asset[]>(`/assets/${id}/children`),

  /**
   * An asset's counters, each with its current value inline (LV §40, §109).
   *
   * Not paginated: the count is bounded by the machine. `latest_value` travels with the
   * counter so a form can show a next-due estimate without a request per meter, and it
   * is `null` for a counter that has never been read — a meter exists before its first
   * reading arrives.
   */
  meters: (assetId: Uuid) => http.get<MeterWithReading[]>(`/assets/${assetId}/meters`),

  /** A counter's history, newest first and paginated (LV §121-122). */
  meterReadings: (
    meterId: Uuid,
    params: ListParams & { date_from?: string; date_to?: string } = {},
  ) => http.get<Page<MeterReading>>(`/assets/meters/${meterId}/readings`, params),

  /** The life-cycle file (LV §17) — an aggregation, not a table. */
  history: (
    id: Uuid,
    params: {
      date_from?: string;
      date_to?: string;
      event_type?: string;
      /** A floor, not an equality test — "WARNING" includes the critical rows. */
      severity?: string;
      include_descendants?: boolean;
    } = {},
  ) => http.get<AssetHistoryEntry[]>(`/assets/${id}/history`, params),

  /**
   * File the life-cycle file as a Nachweis — LV §17, §144.
   *
   * The same filters the screen is showing, so the document states the period and the
   * filters it was issued under. There is no `force`: a life-cycle file certifies a
   * period rather than a single event, so every request legitimately produces a new,
   * separately numbered document.
   */
  generateEvidence: (
    id: Uuid,
    params: {
      date_from?: string;
      date_to?: string;
      event_type?: string;
      severity?: string;
    } = {},
  ) => http.post<DocumentMeta>(`/assets/${id}/evidence${queryOf(params)}`),

  create: (body: Record<string, unknown>) => http.post<Asset>("/assets", body),

  update: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<Asset>(`/assets/${id}`, body),

  /**
   * A removed asset is deactivated, never deleted (LV §16). Kept because it is the
   * endpoint that already exists; `changeStatus("remove", …)` is the same transition
   * reached by its own name. `version` is optional — the backend treats an absent
   * version as "not checked", as it does on every other action endpoint (LV §105).
   */
  deactivate: (id: Uuid, body: { status: string; version?: number; reason?: string }) =>
    http.post<Asset>(`/assets/${id}/deactivate`, body),

  /**
   * The asset lifecycle (LV §16). A status is never sent in `update` — it moves
   * through these named actions, and which ones are legal from the current status
   * is the backend's decision, not this file's (LV §110, §134).
   */
  changeStatus: (
    id: Uuid,
    action: AssetAction,
    body: { reason?: string; version?: number } = {},
  ) => http.post<Asset>(`/assets/${id}/${action}`, body),

  /**
   * Which of the ten actions this user may invoke on this asset right now, filtered
   * by status and by permission. Render buttons from this, never from a table in the
   * client — that would be business logic in the frontend (LV §134).
   */
  actions: (id: Uuid) => http.get<AssetAction[]>(`/assets/${id}/actions`),

  addMeter: (id: Uuid, body: Record<string, unknown>) =>
    http.post<{ id: Uuid; code: string }>(`/assets/${id}/meters`, body),

  recordReading: (meterId: Uuid, body: Record<string, unknown>) =>
    http.post<{ id: Uuid; value: string }>(`/assets/meters/${meterId}/readings`, body),
};

// ---------------------------------------------------------------- work orders (LV §110)
export const workOrderService = {
  list: (
    params: ListParams & {
      site_id?: Uuid;
      /** Narrows within the caller's scope, never widens it (LV §9, #413). */
      region_id?: Uuid;
      asset_id?: Uuid;
      status?: string[];
      order_type?: string;
      priority?: string;
      responsible_team_id?: Uuid;
      responsible_user_id?: Uuid;
      due_before?: string;
      due_after?: string;
      open_only?: boolean;
      revision_id?: Uuid;
    } = {},
  ) => http.get<Page<WorkOrder>>("/work-orders", params),

  get: (id: Uuid) => http.get<WorkOrderDetail>(`/work-orders/${id}`),

  create: (body: Record<string, unknown>) => http.post<WorkOrder>("/work-orders", body),

  /** LV §58-61 — which releases this order needs and which it already holds. */
  safety: (id: Uuid) => http.get<WorkOrderSafetyState>(`/work-orders/${id}/safety`),

  permits: (id: Uuid) => http.get<WorkPermit[]>(`/work-orders/${id}/permits`),

  requestPermit: (
    id: Uuid,
    body: {
      permit_type: string;
      valid_from?: string | null;
      valid_until?: string | null;
      comment?: string | null;
    },
  ) => http.post<WorkPermit>(`/work-orders/${id}/permits`, body),

  // A permit's status moves through named actions, never a PATCH (LV §110).
  permitAction: (
    permitId: Uuid,
    action: string,
    body: { version?: number; reason?: string } = {},
  ) => http.post<WorkPermit>(`/work-orders/permits/${permitId}/${action}`, body),

  /**
   * Data only. `status` is deliberately not accepted here — the server refuses it
   * rather than ignoring it, so a caller cannot believe a transition happened
   * (LV §110, Arch §9). A key left out is left alone; `null` clears it. A changed team
   * is checked there (`TEAM_REFERENCE_UNKNOWN`, `TEAM_INACTIVE`, `CROSS_SITE_REFERENCE`),
   * and so is an end before the start (`PLANNED_END_BEFORE_START`) — #483.
   */
  update: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<WorkOrder>(`/work-orders/${id}`, body),

  deactivate: (id: Uuid, reason?: string) =>
    http.delete<void>(`/work-orders/${id}`, reason ? { reason } : undefined),

  /**
   * The one way a status changes (LV §110).
   *
   * `version` is optional: a tablet coming back online submits a completion without
   * having re-read the order. When supplied, the server checks it.
   */
  action: (
    id: Uuid,
    action: WorkOrderAction,
    body: {
      version?: number;
      reason?: string;
      technical_result?: string;
      completion_comment?: string;
    } = {},
  ) => http.post<WorkOrder>(`/work-orders/${id}/${action}`, body),

  operations: (id: Uuid) => http.get<Operation[]>(`/work-orders/${id}/operations`),

  /**
   * `OperationCreate` (LV §31). The server forbids unknown keys, so the shape is spelled
   * out; whether the order may still take a step, and whether a required team may be
   * planned here, is its answer (#483).
   */
  addOperation: (
    id: Uuid,
    body: {
      title: string;
      instruction?: string;
      planned_duration_minutes?: number;
      required_people?: number;
      required_team_id?: Uuid;
      required_qualification_id?: Uuid;
    },
  ) => http.post<Operation>(`/work-orders/${id}/operations`, body),

  completeOperation: (
    operationId: Uuid,
    body: {
      version?: number;
      completion_text?: string;
      actual_duration_minutes?: number;
    },
  ) => http.post<Operation>(`/work-orders/operations/${operationId}/complete`, body),

  materials: (id: Uuid) =>
    http.get<WorkOrderMaterial[]>(`/work-orders/${id}/materials`),

  planMaterial: (
    id: Uuid,
    body: { material_id: Uuid; planned_quantity: string; estimated_unit_cost?: string },
  ) => http.post<WorkOrderMaterial>(`/work-orders/${id}/materials`, body),

  /** Books the ledger and reports whether the minimum was breached (LV §67-69). */
  issueMaterial: (
    id: Uuid,
    body: {
      material_id: Uuid;
      stock_location_id: Uuid;
      quantity: string;
      unit_cost?: string;
    },
  ) =>
    http.post<{
      transaction_id: Uuid;
      current_stock: string;
      available_stock: string;
      below_minimum: boolean;
      notifications_created: number;
    }>(`/work-orders/${id}/materials/issue`, body),

  /**
   * Bind stock to this order without moving it (LV §70).
   *
   * The one a technician performs before the job rather than during it, and the one
   * LV §127 names as having to survive a dead spot: material promised to Thursday's
   * overhaul must stop showing as available for Tuesday's repair, and the promise is
   * made at the machine.
   */
  reserveMaterial: (
    id: Uuid,
    body: { material_id: Uuid; stock_location_id: Uuid; quantity: string },
  ) => http.post<WorkOrderMaterial>(`/work-orders/${id}/materials/reserve`, body),

  /** Return unused material to stock — a ledger movement like the issue (LV §67, §70). */
  returnMaterial: (
    id: Uuid,
    body: { material_id: Uuid; stock_location_id: Uuid; quantity: string },
  ) =>
    http.post<{
      transaction_id: Uuid;
      current_stock: string;
      available_stock: string;
      below_minimum: boolean;
    }>(`/work-orders/${id}/materials/return`, body),

  // --- LV §58: the three data points that come before a permit ---------------
  /**
   * Which hazard assessment applies to this job, and that it does.
   *
   * `document_id: null` clears it — an assessment attached to the wrong order has to
   * be removable. The server refuses a document that is not of type RISK_ASSESSMENT.
   */
  setHazardAssessment: (id: Uuid, body: { document_id: Uuid | null; note?: string }) =>
    http.put<HazardAssessment>(`/work-orders/${id}/hazard-assessment`, body),

  orderPpe: (id: Uuid) => http.get<WorkOrderPpe[]>(`/work-orders/${id}/ppe`),

  /** What the order's hazard flags suggest, resolved against the catalogue (LV §28). */
  suggestedPpe: (id: Uuid) => http.get<PpeType[]>(`/work-orders/${id}/ppe/suggested`),

  requirePpe: (
    id: Uuid,
    body: { ppe_type_id: Uuid; mandatory?: boolean; note?: string },
  ) => http.post<WorkOrderPpe>(`/work-orders/${id}/ppe`, body),

  /** Soft delete, like every removal of business data (LV §11). */
  removePpe: (id: Uuid, ppeRowId: Uuid, reason?: string) =>
    http.delete<void>(`/work-orders/${id}/ppe/${ppeRowId}`, { reason }),

  briefings: (id: Uuid) => http.get<SafetyBriefing[]>(`/work-orders/${id}/briefings`),

  /** One person, one row. `briefed_by` is the caller and cannot be supplied. */
  recordBriefing: (
    id: Uuid,
    body: {
      topic: string;
      briefed_user_id?: Uuid;
      briefed_person_name?: string;
      document_id?: Uuid;
    },
  ) => http.post<SafetyBriefing>(`/work-orders/${id}/briefings`, body),

  /** Refused for anybody but the person the briefing names, and the attempt is audited. */
  acknowledgeBriefing: (id: Uuid, briefingId: Uuid) =>
    http.post<SafetyBriefing>(`/work-orders/${id}/briefings/${briefingId}/acknowledge`),

  recalculateCosts: (id: Uuid) =>
    http.post<WorkOrder>(`/work-orders/${id}/recalculate-costs`),

  timeEntries: (id: Uuid) => http.get<TimeEntry[]>(`/work-orders/${id}/time-entries`),

  /**
   * Book internal hours (LV §93).
   *
   * `hours` is a string: a decimal quantity must not travel as a float (LV §117).
   * Everything else is optional because the common case is a technician recording
   * work just finished — the server defaults `user_id` to the caller and `booked_on`
   * to today, and booking against somebody else needs the planning permission.
   */
  bookTime: (
    id: Uuid,
    body: {
      hours: string;
      booked_on?: DateOnly;
      user_id?: Uuid;
      operation_id?: Uuid;
      hourly_rate?: string;
      comment?: string;
    },
  ) => http.post<TimeEntry>(`/work-orders/${id}/time-entries`, body),
};

// ---------------------------------------------------------------- maintenance (LV §111)
export const workPlanService = {
  list: (
    params: ListParams & {
      status?: string;
      asset_category_id?: Uuid;
      current_only?: boolean;
    } = {},
  ) => http.get<Page<WorkPlan>>("/work-plans", params),

  get: (id: Uuid) => http.get<WorkPlan>(`/work-plans/${id}`),

  /** Every revision of this plan number, newest first (LV §33). */
  revisions: (id: Uuid) => http.get<WorkPlan[]>(`/work-plans/${id}/revisions`),

  create: (body: {
    name: string;
    description?: string | null;
    asset_category_id?: Uuid | null;
    valid_from?: DateOnly | null;
    valid_until?: DateOnly | null;
  }) => http.post<WorkPlan>("/work-plans", body),

  update: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<WorkPlan>(`/work-plans/${id}`, body),

  addStep: (id: Uuid, body: Record<string, unknown>) =>
    http.post<WorkPlanStep>(`/work-plans/${id}/steps`, body),

  updateStep: (stepId: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<WorkPlanStep>(`/work-plans/steps/${stepId}`, body),

  removeStep: (stepId: Uuid, reason?: string) =>
    http.delete<void>(`/work-plans/steps/${stepId}`, { reason }),

  /** What a step consumes (LV §32, §70). */
  stepMaterials: (stepId: Uuid) =>
    http.get<WorkPlanStepMaterial[]>(`/work-plans/steps/${stepId}/materials`),

  addStepMaterial: (stepId: Uuid, body: { material_id: Uuid; quantity: string }) =>
    http.post<WorkPlanStepMaterial>(`/work-plans/steps/${stepId}/materials`, body),

  removeStepMaterial: (stepMaterialId: Uuid, reason?: string) =>
    http.delete<void>(`/work-plans/steps/materials/${stepMaterialId}`, { reason }),

  /**
   * The instruments a step may name (LV §32, §48). Read-only — maintaining the
   * instrument master has no screen, which is a recorded gap and not this call's job.
   */
  measuringInstruments: () =>
    http.get<MeasuringInstrument[]>("/work-plans/measuring-instruments"),

  // A status change is an action, never `PATCH status` (LV §110).
  action: (
    id: Uuid,
    action: string,
    body: { version?: number; reason?: string } = {},
  ) => http.post<WorkPlan>(`/work-plans/${id}/${action}`, body),

  /** The LV §33 correction path: revision n+1 in draft, n untouched. */
  revise: (id: Uuid, reason?: string) =>
    http.post<WorkPlan>(`/work-plans/${id}/revise`, { reason }),
};

export const maintenanceService = {
  list: (
    params: ListParams & {
      site_id?: Uuid;
      /** Narrows within the caller's scope, never widens it (LV §9, #413). */
      region_id?: Uuid;
      asset_id?: Uuid;
      active?: boolean;
      status?: string;
      scheduling_type?: string;
      /** Inclusive, like `due_before`. Needed for the exclusive LV §146 bands. */
      due_after?: string;
      due_before?: string;
      search?: string;
    } = {},
  ) => http.get<Page<MaintenancePlan>>("/maintenance-plans", params),

  get: (id: Uuid) => http.get<MaintenancePlan>(`/maintenance-plans/${id}`),

  create: (body: Record<string, unknown>) =>
    http.post<MaintenancePlan>("/maintenance-plans", body),

  /**
   * An interval change requires `reason` (LV §104) plus `supporting_data` and
   * `approved_by` (LV §189). The server decides whether a given request *is* an
   * interval change and whether the named approver may release plans at that site —
   * neither is knowable here, and both are business decisions (LV §134).
   */
  update: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<MaintenancePlan>(`/maintenance-plans/${id}`, body),

  /** Why this plan's interval is what it is, change by change (LV §189). */
  intervalChanges: (id: Uuid) =>
    http.get<MaintenanceIntervalChange[]>(`/maintenance-plans/${id}/interval-changes`),

  activate: (id: Uuid, version: number) =>
    http.post<MaintenancePlan>(`/maintenance-plans/${id}/activate`, { version }),

  /** `reason` is mandatory here (LV §104). */
  deactivate: (id: Uuid, body: { version: number; reason: string }) =>
    http.post<MaintenancePlan>(`/maintenance-plans/${id}/deactivate`, body),

  /** Which plan due-date produced which work order (LV §39). */
  calls: (id: Uuid) => http.get<MaintenanceCall[]>(`/maintenance-plans/${id}/calls`),

  /** Read-only preview — nothing is created (LV §37). */
  simulate: (id: Uuid, count = 12) =>
    http.post<SimulationResponse>(`/maintenance-plans/${id}/simulate-next-dates`, {
      count,
    }),
};

// ---------------------------------------------------------------- inspections (LV §112)
export const inspectionService = {
  list: (
    params: ListParams & {
      site_id?: Uuid;
      /** Narrows within the caller's scope, never widens it (LV §9, #413). */
      region_id?: Uuid;
      asset_id?: Uuid;
      status?: string[];
      inspection_type?: string;
      inspector_id?: Uuid;
      condition_class?: string;
      performed_from?: string;
      performed_to?: string;
    } = {},
  ) => http.get<Page<Inspection>>("/inspections", params),

  get: (id: Uuid) => http.get<InspectionDetail>(`/inspections/${id}`),

  create: (body: Record<string, unknown>) =>
    http.post<Inspection>("/inspections", body),

  start: (id: Uuid, version: number) =>
    http.post<Inspection>(`/inspections/${id}/start`, { version }),

  /**
   * Completing an inspection derives its findings and raises the follow-up orders in
   * one transaction (LV §170). The response says what it produced.
   */
  complete: (
    id: Uuid,
    body: {
      version: number;
      answers: Record<string, unknown>[];
      summary?: string;
      recommendation?: string;
    },
  ) => http.post<InspectionCompletion>(`/inspections/${id}/complete`, body),

  approve: (id: Uuid, version: number) =>
    http.post<Inspection>(`/inspections/${id}/approve`, { version }),

  addFinding: (id: Uuid, body: Record<string, unknown>) =>
    http.post<Finding>(`/inspections/${id}/findings`, body),

  addMeasurement: (id: Uuid, body: Record<string, unknown>) =>
    http.post<Measurement>(`/inspections/${id}/measurements`, body),

  findings: (
    params: ListParams & {
      /** Narrows within the caller's scope, never widens it (LV §9, #413). */
      region_id?: Uuid;
      asset_id?: Uuid;
      inspection_id?: Uuid;
      severity?: string[];
      status?: string[];
      open_only?: boolean;
    } = {},
  ) => http.get<Page<Finding>>("/findings", params),
};

// ---------------------------------------------------------------- failures
export const failureService = {
  list: (
    params: ListParams & {
      site_id?: Uuid;
      /** Narrows within the caller's scope, never widens it (LV §9, #413). */
      region_id?: Uuid;
      asset_id?: Uuid;
      status?: string[];
      failure_type?: string;
      open_only?: boolean;
      detected_from?: string;
      detected_to?: string;
    } = {},
  ) => http.get<Page<Failure>>("/failures", params),

  get: (id: Uuid) => http.get<Failure>(`/failures/${id}`),

  report: (body: Record<string, unknown>) =>
    http.post<FailureReportResponse>("/failures", body),

  advance: (
    id: Uuid,
    target: string,
    body: Record<string, unknown> & { version: number },
  ) => http.post<Failure>(`/failures/${id}/advance`, body, { target }),

  addRca: (id: Uuid, body: Record<string, unknown>) =>
    http.post<{ id: Uuid }>(`/failures/${id}/rca`, body),

  /** Shown while the fault is still being typed (LV §51). */
  repeatCheck: (assetId: Uuid, failureMode?: string) =>
    http.get<RepeatAssessment>(`/failures/repeat-check/${assetId}`, {
      failure_mode: failureMode,
    }),
};

// ---------------------------------------------------------------- risk (LV §53-57)
export const riskService = {
  assessCriticality: (assetId: Uuid, body: Record<string, unknown>) =>
    http.post<CriticalityAssessment>(`/risk/assets/${assetId}/criticality`, body),

  criticalityHistory: (assetId: Uuid) =>
    http.get<CriticalityAssessment[]>(`/risk/assets/${assetId}/criticality`),

  /** Read-only: try the questionnaire without committing to it (LV §57). */
  recommendStrategy: (assetId: Uuid, answers: Record<string, string>) =>
    http.post<StrategyRecommendation>(
      `/risk/assets/${assetId}/strategy/recommend`,
      answers,
    ),

  assessStrategy: (assetId: Uuid, body: Record<string, unknown>) =>
    http.post<RcmAssessment>(`/risk/assets/${assetId}/strategy`, body),

  strategyHistory: (assetId: Uuid) =>
    http.get<RcmAssessment[]>(`/risk/assets/${assetId}/strategy`),

  approveStrategy: (assessmentId: Uuid, version: number) =>
    http.post<RcmAssessment>(`/risk/rcm-assessments/${assessmentId}/approve`, {
      version,
    }),
};

// ---------------------------------------------------------------- Lagerorte (LV §66, #459)
export const stockLocationService = {
  /**
   * The administration list. `GET /inventory` (`inventoryService.locations`) stays what
   * every picker reads — the active ones; this one carries the inactive on request,
   * each row's plant code and the actions the server allows the caller there.
   */
  list: (params: { site_id?: Uuid; include_inactive?: boolean } = {}) =>
    http.get<StockLocationAdmin[]>("/inventory/locations", params),

  /** The plants at which the caller may create a Lagerort, decided by the server. */
  manageableSites: () =>
    http.get<ManageableSite[]>("/inventory/locations/manageable-sites"),

  /** `code` is upper-cased by the server; plant and code are fixed from here on. */
  create: (body: {
    site_id: Uuid;
    code: string;
    name: string;
    description?: string | null;
  }) => http.post<StockLocationAdmin>("/inventory/locations", body),

  /**
   * Name and description only. `site_id`, `code` and `active` are refused as unknown
   * fields, and a stale `version` answers `409` without overwriting (LV §105).
   */
  update: (
    id: Uuid,
    body: {
      version: number;
      name?: string;
      description?: string | null;
      reason?: string;
    },
  ) => http.patch<StockLocationAdmin>(`/inventory/locations/${id}`, body),

  /** Refused while stock, an open reservation or a stock policy keeps it in use. */
  deactivate: (id: Uuid, version: number, reason: string) =>
    http.post<StockLocationAdmin>(`/inventory/locations/${id}/deactivate`, {
      version,
      reason,
    }),

  activate: (id: Uuid, version: number, reason: string) =>
    http.post<StockLocationAdmin>(`/inventory/locations/${id}/activate`, {
      version,
      reason,
    }),
};

// ---------------------------------------------------------------- inventory (LV §113)
export const inventoryService = {
  materials: (
    params: ListParams & {
      criticality?: string;
      obsolescence_status?: string;
      active?: boolean;
      asset_id?: Uuid;
      /**
       * Narrows the stock columns — free, reserved — to the region's stores. The rows
       * stay the group-wide material master (LV §9, #413, #468).
       */
      region_id?: Uuid;
    } = {},
  ) => http.get<Page<MaterialListItem>>("/materials", params),

  material: (id: Uuid) => http.get<Material>(`/materials/${id}`),

  createMaterial: (body: Record<string, unknown>) =>
    http.post<Material>("/materials", body),

  updateMaterial: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<Material>(`/materials/${id}`, body),

  /** Computed from the ledger on every call (LV §67). */
  stock: (materialId: Uuid) =>
    http.get<StockStatus[]>(`/materials/${materialId}/stock`),

  movements: (
    materialId: Uuid,
    params: ListParams & {
      stock_location_id?: Uuid;
      transaction_type?: string;
      date_from?: string;
      date_to?: string;
    } = {},
  ) => http.get<Page<StockTransaction>>(`/materials/${materialId}/movements`, params),

  /** `region_id` narrows the stores to the region's plants, never widens (#468). */
  locations: (params: { region_id?: Uuid } = {}) =>
    http.get<StockLocation[]>("/inventory", params),

  /** The client sends a magnitude; the sign comes from the type server-side (LV §67). */
  book: (body: Record<string, unknown>) =>
    http.post<BookingResponse>("/inventory/transactions", body),

  transfer: (body: Record<string, unknown>) =>
    http.post<{ transfer_group_id: Uuid; source_stock: string; target_stock: string }>(
      "/inventory/transfers",
      body,
    ),

  /**
   * Sperren / Freigeben — decision 162. Each writes two ledger rows that cancel out; the
   * server checks the permission at the location's site, the quantity and the reason.
   */
  block: (body: {
    material_id: Uuid;
    stock_location_id: Uuid;
    quantity: string;
    reason: string;
  }) => http.post<StockBlockResponse>("/inventory/block", body),

  unblock: (body: {
    material_id: Uuid;
    stock_location_id: Uuid;
    quantity: string;
    reason: string;
  }) => http.post<StockBlockResponse>("/inventory/unblock", body),

  rebuildCache: () =>
    http.post<{ ok: boolean; message: string | null }>("/inventory/rebuild-cache"),

  // ---- spare-part list (LV §65)
  assetsForMaterial: (materialId: Uuid) =>
    http.get<MaterialAssetRelation[]>(`/materials/${materialId}/assets`),

  bom: (assetId: Uuid) =>
    http.get<MaterialAssetRelation[]>("/inventory/bom", { asset_id: assetId }),

  linkMaterialToAsset: (materialId: Uuid, body: Record<string, unknown>) =>
    http.post<MaterialAssetRelation>(`/materials/${materialId}/assets`, body),

  updateMaterialAssetRelation: (
    materialId: Uuid,
    relationId: Uuid,
    body: Record<string, unknown> & { version: number },
  ) =>
    http.patch<MaterialAssetRelation>(
      `/materials/${materialId}/assets/${relationId}`,
      body,
    ),

  unlinkMaterialFromAsset: (materialId: Uuid, relationId: Uuid) =>
    http.delete<void>(`/materials/${materialId}/assets/${relationId}`),

  // ---- stock policies (LV §69)
  stockPolicies: (
    params: ListParams & {
      material_id?: Uuid;
      stock_location_id?: Uuid;
      site_id?: Uuid;
      /** Narrows within the caller's scope, never widens it (LV §9, #413). */
      region_id?: Uuid;
    } = {},
  ) => http.get<Page<StockPolicy>>("/inventory/stock-policies", params),

  /** Idempotent on the (material, location) pair — a second call amends the rule. */
  setStockPolicy: (body: Record<string, unknown>) =>
    http.post<StockPolicy>("/inventory/stock-policies", body),

  updateStockPolicy: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<StockPolicy>(`/inventory/stock-policies/${id}`, body),

  deleteStockPolicy: (id: Uuid) => http.delete<void>(`/inventory/stock-policies/${id}`),

  /** The shortfall list the LOW_STOCK notification is generated from. */
  lowStock: (params: { site_id?: Uuid; include_reorder_point?: boolean } = {}) =>
    http.get<LowStockRow[]>("/inventory/low-stock", params),

  // ---- Nachbestellanforderungen (#466, decision 170)
  /**
   * Filtered and paginated server-side, newest first, over the plants the caller reads.
   * `status` repeats (`status=OPEN&status=ORDERED`); left out, every status is listed.
   * `region_id` narrows within what the caller may read, never widens it (#413).
   */
  reorderRequests: (
    params: ListParams & {
      status?: ReorderRequestStatus[];
      material_id?: Uuid;
      stock_location_id?: Uuid;
      site_id?: Uuid;
      region_id?: Uuid;
    } = {},
  ) => http.get<Page<ReorderRequest>>("/inventory/reorder-requests", params),

  reorderRequest: (id: Uuid) =>
    http.get<ReorderRequest>(`/inventory/reorder-requests/${id}`),

  /** Creates the request as `OPEN`; the server refuses a second active one per pair. */
  createReorderRequest: (body: {
    material_id: Uuid;
    stock_location_id: Uuid;
    quantity: string;
    comment: string | null;
  }) => http.post<ReorderRequest>("/inventory/reorder-requests", body),

  /** `OPEN` → `ORDERED`. A dedicated action, never a PATCH on `status` (LV §110). */
  orderReorderRequest: (
    id: Uuid,
    body: { version: number; purchase_order: string | null; comment: string | null },
  ) => http.post<ReorderRequest>(`/inventory/reorder-requests/${id}/order`, body),

  /** `OPEN` or `ORDERED` → `CANCELLED`, with a reason the audit entry carries. */
  cancelReorderRequest: (id: Uuid, body: { version: number; reason: string }) =>
    http.post<ReorderRequest>(`/inventory/reorder-requests/${id}/cancel`, body),
};

// ---------------------------------------------------------------- documents (LV §114)
export const documentService = {
  list: (
    params: ListParams & {
      document_type?: string;
      status?: string[];
      entity_type?: string;
      entity_id?: Uuid;
      current_only?: boolean;
      valid_on?: string;
    } = {},
  ) => http.get<Page<DocumentMeta>>("/documents", params),

  get: (id: Uuid) => http.get<DocumentMeta>(`/documents/${id}`),

  /** The nine-step upload sequence of LV §75 runs server-side. */
  upload: (
    file: File,
    meta: {
      title: string;
      document_type: string;
      valid_from?: string;
      valid_until?: string;
      entity_type?: string;
      entity_id?: Uuid;
      relationship_type?: string;
    },
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("title", meta.title);
    form.append("document_type", meta.document_type);
    for (const key of [
      "valid_from",
      "valid_until",
      "entity_type",
      "entity_id",
      "relationship_type",
    ] as const) {
      const value = meta[key];
      if (value) form.append(key, value);
    }
    return http.upload<DocumentMeta>("/documents", form);
  },

  /**
   * Downloads are authorised, hash-verified and audited server-side (LV §75, §102),
   * which is why this fetches a blob rather than pointing an `<a href>` at a URL.
   */
  download: async (id: Uuid, filename: string): Promise<void> => {
    const response = await http.download(`/documents/${id}/download`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },

  submitForReview: (id: Uuid, version: number) =>
    http.post<DocumentMeta>(`/documents/${id}/submit-for-review`, { version }),

  approve: (
    id: Uuid,
    body: { version: number; approval_type?: string; comment?: string },
  ) => http.post<DocumentMeta>(`/documents/${id}/approve`, body),

  /**
   * Creates the next revision. There is no endpoint that overwrites an approved
   * document (LV §76), and `reason` is mandatory (LV §104).
   */
  supersede: (
    id: Uuid,
    file: File,
    meta: { reason: string; title?: string; valid_from?: string; valid_until?: string },
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("reason", meta.reason);
    if (meta.title) form.append("title", meta.title);
    if (meta.valid_from) form.append("valid_from", meta.valid_from);
    if (meta.valid_until) form.append("valid_until", meta.valid_until);
    return http.upload<DocumentMeta>(`/documents/${id}/supersede`, form);
  },

  /** Every revision of a document number — both stay retrievable (LV §76). */
  revisions: (documentNumber: string) =>
    http.get<DocumentMeta[]>(`/documents/${documentNumber}/revisions`),

  approvals: (documentNumber: string) =>
    http.get<DocumentApproval[]>(`/documents/${documentNumber}/approvals`),

  link: (
    id: Uuid,
    body: { entity_type: string; entity_id: Uuid; relationship_type?: string },
  ) => http.post<{ id: Uuid }>(`/documents/${id}/links`, body),
};

// ---------------------------------------------------------------- reporting (LV §115)
export const reportingService = {
  /** Every figure is computed server-side (LV §88). */
  dashboard: (
    params: {
      site_id?: Uuid;
      region_id?: Uuid;
      date_from?: string;
      date_to?: string;
    } = {},
  ) => http.get<Dashboard>("/dashboard", params),

  kpis: (
    params: {
      site_id?: Uuid;
      region_id?: Uuid;
      date_from?: string;
      date_to?: string;
    } = {},
  ) => http.get<Kpi[]>("/kpis", params),

  report: (
    name:
      | "work-orders"
      | "maintenance"
      | "failures"
      | "inventory"
      | "costs"
      | "compliance"
      | "maintenance-preview"
      | "revision-status",
    params: {
      site_id?: Uuid;
      region_id?: Uuid;
      date_from?: string;
      date_to?: string;
    } = {},
  ) => http.get<Record<string, unknown>>(`/reports/${name}`, params),

  /**
   * Wartungsvorschau 30/60/90 — LV §146.
   *
   * Typed rather than folded into `report()` because a screen renders it: the band
   * counts and their bounds come back from the server and are displayed as they
   * arrive. The page never works out which band a plan is in.
   */
  maintenancePreview: (params: { site_id?: Uuid; region_id?: Uuid } = {}) =>
    http.get<MaintenancePreviewReport>("/reports/maintenance-preview", params),

  /** Revisionsstatus — LV §79-80, §146. Counts by status and by gate. */
  revisionStatus: (params: { site_id?: Uuid; region_id?: Uuid } = {}) =>
    http.get<RevisionStatusReport>("/reports/revision-status", params),

  /**
   * Download one of the six standard reports as CSV, Excel or PDF (LV §145).
   *
   * A blob rather than an `<a href>`: the export is authorised and audited
   * server-side (LV §102, §217), so it has to carry the bearer token — and a plain
   * link cannot. The filename comes from `Content-Disposition`, which the server sets
   * in both the ASCII and the RFC 5987 form so `Störungsbericht` survives.
   */
  downloadReport: async (
    name: string,
    format: "CSV" | "XLSX" | "PDF",
    params: {
      site_id?: Uuid;
      region_id?: Uuid;
      date_from?: string;
      date_to?: string;
    } = {},
  ): Promise<void> => {
    const response = await http.download(
      `/reports/${name}${buildReportQuery({ ...params, format })}`,
    );
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filenameFrom(response) ?? `${name}.${format.toLowerCase()}`;
    link.click();
    URL.revokeObjectURL(url);
  },

  /** LV §217 — the change history is queued and rendered by the worker. */
  queueAuditExport: (
    format: "CSV" | "XLSX" | "PDF",
    params: { entity_type?: string; date_from?: string; date_to?: string } = {},
  ) => http.post<ExportJob>(`/exports/audit${buildReportQuery({ ...params, format })}`),

  exportJob: (id: Uuid) => http.get<ExportJob>(`/exports/${id}`),
};

// ---------------------------------------------------------------- audit (LV §101)
export const auditService = {
  list: (
    params: ListParams & {
      entity_type?: string;
      entity_id?: Uuid;
      user_id?: Uuid;
      action?: string[];
      denied_only?: boolean;
      date_from?: string;
      date_to?: string;
    } = {},
  ) => http.get<Page<AuditEntry>>("/audit", params),

  /** The change history shown in an entity's detail drawer. */
  forEntity: (entityType: string, entityId: Uuid) =>
    http.get<AuditEntry[]>(`/audit/${entityType}/${entityId}`),
};

// ---------------------------------------------------------------- notifications
export const notificationService = {
  list: (params: ListParams & { unread_only?: boolean } = {}) =>
    http.get<Page<Notification>>("/notifications", params),
  unreadCount: () => http.get<{ unread: number }>("/notifications/unread-count"),
  markRead: (id: Uuid) => http.post<Notification>(`/notifications/${id}/read`),
  markAllRead: () => http.post<{ ok: boolean }>("/notifications/read-all"),
};

// ---------------------------------------------------------------- data quality
export const qualityService = {
  /**
   * The aggregate figure and every rule's standing (LV §191). `region_id` narrows to a
   * Regionalbereich's plants and never widens the caller's scope (#468).
   */
  report: (params: { site_id?: Uuid; region_id?: Uuid } = {}) =>
    http.get<DataQualityReport>("/data-quality", params),

  /** The offending rows for one rule, so the figure can be worked off. */
  violations: (code: string, params: { site_id?: Uuid; region_id?: Uuid } = {}) =>
    http.get<DataQualityViolations>(`/data-quality/${code}`, params),
};

// ---------------------------------------------------------------- global search (LV §120)
/**
 * One search across every object type the caller may read.
 *
 * A thin wrapper, like every service here, and pointedly so: the ranking, the site
 * scoping, the per-type permission filter and the caps are all the server's decisions
 * (LV §120, §134). The query is its only argument, so the per-type cap that applies is
 * always the one configured on the server (`search_limit_per_type`). A number here would
 * be a display rule kept in the wrong place, and it would quietly defeat that setting.
 */
export const searchService = {
  /**
   * `q` is passed as the user typed it. Trimming and the minimum length are the
   * server's rules, and a `422 SEARCH_QUERY_TOO_SHORT` is the answer to a query that
   * is too short — re-implementing the check here would mean two versions of it.
   */
  global: (q: string) => http.get<SearchResponse>("/search", { q }),
};

// ---------------------------------------------------------------- configuration
export const configurationService = {
  /** The German label map (LV §123-126). No German string for a backend value is
   *  hard-coded in this codebase. */
  enums: () => http.get<EnumCatalogue>("/configuration/enums"),
  priorities: (includeInactive = false, includeDeleted = false) =>
    http.get<PriorityDefinition[]>("/configuration/priorities", {
      include_inactive: includeInactive || undefined,
      include_deleted: includeDeleted || undefined,
    }),

  // --- LV §186: the write side. Every one of these needs `configuration.manage`;
  // the reads above deliberately do not, so an auditor can see the values in force.
  createPriority: (body: {
    code: string;
    name: string;
    description?: string | null;
    reaction_time_minutes?: number | null;
    target_completion_minutes?: number | null;
    escalation_time_minutes?: number | null;
    sort_order?: number;
    notify_on_create?: boolean;
  }) => http.post<PriorityDefinition>("/configuration/priorities", body),

  /** `version` is required — a stale one answers 409 without overwriting (LV §105). */
  updatePriority: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<PriorityDefinition>(`/configuration/priorities/${id}`, body),

  /**
   * Withdraw one — an action, never a field on a PATCH (Arch §9, LV §110).
   *
   * Refused while open orders or follow-up rules still name the code, because an
   * inactive priority is refused by the server exactly as a missing one would be.
   */
  deactivatePriority: (id: Uuid, reason?: string) =>
    http.post<PriorityDefinition>(`/configuration/priorities/${id}/deactivate`, {
      reason,
    }),

  activatePriority: (id: Uuid, reason: string) =>
    http.post<PriorityDefinition>(`/configuration/priorities/${id}/activate`, {
      reason,
    }),

  /** What still points at a priority, so the screen can say why before it is tried. */
  priorityUsage: (id: Uuid) =>
    http.get<{ usages: Record<string, number> }>(
      `/configuration/priorities/${id}/usage`,
    ),

  /**
   * Soft delete and restore (LV §11, §108).
   *
   * Both endpoints shipped with the first slice and neither had a caller, which is the
   * shape LV §223 forbids. A deleted row is visible on the administration list via
   * `include_deleted`, which is what gives the restore something to act on.
   */
  deletePriority: (id: Uuid, reason: string) =>
    http.delete<void>(`/configuration/priorities/${id}`, { reason }),
  restorePriority: (id: Uuid, reason: string) =>
    http.post<PriorityDefinition>(`/configuration/priorities/${id}/restore`, {
      reason,
    }),

  roles: () => http.get<RoleDefinition[]>("/configuration/roles"),

  qualifications: (includeInactive = false, includeDeleted = false) =>
    http.get<QualificationDefinition[]>("/configuration/qualifications", {
      include_inactive: includeInactive || undefined,
      include_deleted: includeDeleted || undefined,
    }),
  createQualification: (body: {
    code: string;
    name: string;
    description?: string | null;
    validity_months?: number | null;
    category?: string | null;
  }) => http.post<QualificationDefinition>("/configuration/qualifications", body),
  updateQualification: (
    id: Uuid,
    body: Record<string, unknown> & { version: number },
  ) => http.patch<QualificationDefinition>(`/configuration/qualifications/${id}`, body),
  deactivateQualification: (id: Uuid, reason?: string) =>
    http.post<QualificationDefinition>(
      `/configuration/qualifications/${id}/deactivate`,
      { reason },
    ),
  activateQualification: (id: Uuid, reason: string) =>
    http.post<QualificationDefinition>(`/configuration/qualifications/${id}/activate`, {
      reason,
    }),
  deleteQualification: (id: Uuid, reason: string) =>
    http.delete<void>(`/configuration/qualifications/${id}`, { reason }),
  restoreQualification: (id: Uuid, reason: string) =>
    http.post<QualificationDefinition>(`/configuration/qualifications/${id}/restore`, {
      reason,
    }),
  /** Held certificates, task-plan steps and order operations that still require it. */
  qualificationUsage: (id: Uuid) =>
    http.get<{ usages: Record<string, number> }>(
      `/configuration/qualifications/${id}/usage`,
    ),

  assetCategories: (includeInactive = false, includeDeleted = false) =>
    http.get<AssetCategoryDefinition[]>("/configuration/asset-categories", {
      include_inactive: includeInactive || undefined,
      include_deleted: includeDeleted || undefined,
    }),
  createAssetCategory: (body: {
    code: string;
    name: string;
    description?: string | null;
    parent_code?: string | null;
  }) => http.post<AssetCategoryDefinition>("/configuration/asset-categories", body),
  /** An explicit `parent_code: null` detaches; omitting the key leaves the parent. */
  updateAssetCategory: (
    id: Uuid,
    body: Record<string, unknown> & { version: number },
  ) =>
    http.patch<AssetCategoryDefinition>(`/configuration/asset-categories/${id}`, body),
  deactivateAssetCategory: (id: Uuid, reason?: string) =>
    http.post<AssetCategoryDefinition>(
      `/configuration/asset-categories/${id}/deactivate`,
      { reason },
    ),
  activateAssetCategory: (id: Uuid, reason: string) =>
    http.post<AssetCategoryDefinition>(
      `/configuration/asset-categories/${id}/activate`,
      { reason },
    ),
  deleteAssetCategory: (id: Uuid, reason: string) =>
    http.delete<void>(`/configuration/asset-categories/${id}`, { reason }),
  restoreAssetCategory: (id: Uuid, reason: string) =>
    http.post<AssetCategoryDefinition>(
      `/configuration/asset-categories/${id}/restore`,
      { reason },
    ),
  /** Assets, plans, templates, obligations, field rules and subcategories. */
  assetCategoryUsage: (id: Uuid) =>
    http.get<{ usages: Record<string, number> }>(
      `/configuration/asset-categories/${id}/usage`,
    ),
  /** The PPE catalogue — configuration, never an enum (LV §58, §28, §187). */
  ppeTypes: () => http.get<PpeType[]>("/configuration/ppe-types"),
  /**
   * The ten LV §60 hazard flags and their German words.
   *
   * Served rather than repeated here: the refusal a planner sees quotes the same map,
   * and the create form offering two of the ten is what happens when the list lives in
   * a component.
   */
  hazardFlags: () => http.get<Record<string, string>>("/configuration/hazard-flags"),
  settings: (siteId?: Uuid) =>
    http.get<ConfigSetting[]>("/configuration/settings", { site_id: siteId }),
  criticalityModel: (profileCode = "DEFAULT") =>
    http.get<CriticalityModel>("/configuration/criticality-model", {
      profile_code: profileCode,
    }),

  /**
   * Save the whole profile in one call — never a PATCH per row.
   *
   * The weights must total exactly 100 % and the bands must cover 0-100 with no gap.
   * Both are invariants of the *set*: saving one row at a time passes through states
   * that violate them, and an asset scored in that moment is scored wrongly.
   */
  saveCriticalityModel: (body: {
    profile_code: string;
    weights: { dimension: string; weight_percent: string; version: number | null }[];
    thresholds: {
      criticality_class: string;
      min_score: string;
      max_score: string;
      version: number | null;
    }[];
    reason?: string;
  }) => http.post<CriticalityModel>("/configuration/criticality-model", body),

  /** `version` is required — a stale one answers 409 without overwriting (LV §105). */
  updateSetting: (
    id: Uuid,
    body: { value: string; version: number; reason?: string },
  ) => http.patch<ConfigSetting>(`/configuration/settings/${id}`, body),

  /** Creates or revises one site's own value for an existing key. */
  setSettingOverride: (body: { key: string; site_id: Uuid; value: string }) =>
    http.post<ConfigSetting>("/configuration/settings", body),

  /** Only an override; the group-wide row is the setting itself and may not go. */
  deleteSettingOverride: (id: Uuid, reason: string) =>
    http.delete<void>(`/configuration/settings/${id}`, { reason }),

  /**
   * The retention periods as editable rows — LV §186, §214-215.
   *
   * A second read beside `/retention-policies`, which is an *assessment* (the period
   * plus what currently sits in each window) and goes through a service that has no
   * write method by design. Editing a period and performing a disposal are different
   * acts, and they stay in different places.
   */
  retentionPolicyDefinitions: () =>
    http.get<RetentionPolicyDefinition[]>(
      "/configuration/retention-policy-definitions",
    ),

  updateRetentionPolicy: (
    id: Uuid,
    body: Record<string, unknown> & { version: number },
  ) =>
    http.patch<RetentionPolicyDefinition>(
      `/configuration/retention-policy-definitions/${id}`,
      body,
    ),

  featureFlags: () => http.get<FeatureFlags>("/configuration/feature-flags"),
};

// ---------------------------------------------------------------- revisions (LV §79-82)
export const revisionService = {
  list: (
    params: ListParams & {
      site_id?: Uuid;
      /** Narrows within the caller's scope, never widens it (LV §9, #413). */
      region_id?: Uuid;
      status?: string[];
      current_gate?: string;
    } = {},
  ) => http.get<Page<Revision>>("/revisions", params),

  get: (id: Uuid) => http.get<Revision>(`/revisions/${id}`),

  create: (body: Record<string, unknown>) => http.post<Revision>("/revisions", body),

  update: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<Revision>(`/revisions/${id}`, body),

  /** The current gate with every criterion and its state (LV §81). */
  gate: (id: Uuid) => http.get<GateStatus>(`/revisions/${id}/gate`),

  /**
   * File the Revisionsbericht — LV §79-82, §144.
   *
   * Only for a closed revision; the server decides that, not this call. `force`
   * produces a *new* document with its own number and hash and never replaces the
   * previous one (LV §76).
   */
  generateEvidence: (id: Uuid, force = false) =>
    http.post<DocumentMeta>(`/revisions/${id}/evidence${force ? "?force=true" : ""}`),

  /** Confirm or withdraw one criterion. Records who and when. */
  confirmCheck: (
    id: Uuid,
    body: {
      requirement_code: string;
      fulfilled: boolean;
      comment?: string;
      evidence_document_id?: Uuid;
    },
  ) => http.post<GateStatus>(`/revisions/${id}/gate-checks`, body),

  /**
   * Close the current gate. Refused with the list of unmet criteria unless every
   * mandatory one is confirmed (LV §81).
   */
  advanceGate: (id: Uuid, body: { version?: number; reason?: string } = {}) =>
    http.post<Revision>(`/revisions/${id}/advance-gate`, body),

  workOrders: (id: Uuid) =>
    http.get<{ id: Uuid; work_order_id: Uuid; sequence: number }[]>(
      `/revisions/${id}/work-orders`,
    ),

  addWorkOrder: (id: Uuid, workOrderId: Uuid, sequence = 0) =>
    http.post<{ id: Uuid }>(`/revisions/${id}/work-orders`, undefined, {
      work_order_id: workOrderId,
      sequence,
    }),
};

// ---------------------------------------------------------------- compliance (LV §84-87)
export const complianceService = {
  standards: (
    params: ListParams & {
      status?: string[];
      issuer?: string;
      review_due_before?: string;
    } = {},
  ) => http.get<Page<Standard>>("/compliance/standards", params),

  createStandard: (body: Record<string, unknown>) =>
    http.post<Standard>("/compliance/standards", body),

  /**
   * Register transitions (LV §85). `confirm-review` may carry the next review date —
   * there is no PATCH on a standard, so this is the only place it can be named, and
   * an entry confirmed without one has its pending review date cleared.
   *
   * Which actions are offered comes from the backend, never from a status check in
   * the client (LV §134).
   */
  changeStandardStatus: (
    id: Uuid,
    action: StandardAction,
    body: { reason?: string; version?: number; next_review_at?: DateOnly } = {},
  ) => http.post<Standard>(`/compliance/standards/${id}/${action}`, body),

  requirements: (
    params: ListParams & {
      standard_id?: Uuid;
      site_id?: Uuid;
      /**
       * Narrows to the region's plants, never widens. Group-wide requirements have no
       * plant and stay listed in every region (LV §9, #413, #468).
       */
      region_id?: Uuid;
      status?: string[];
      owner_id?: Uuid;
      overdue?: boolean;
    } = {},
  ) => http.get<Page<ComplianceRequirement>>("/compliance/requirements", params),

  createRequirement: (body: Record<string, unknown>) =>
    http.post<ComplianceRequirement>("/compliance/requirements", body),

  updateRequirement: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<ComplianceRequirement>(`/compliance/requirements/${id}`, body),

  /**
   * Requirement transitions (LV §87). `implement` may carry the evidence document
   * with it — attaching the proof and declaring the requirement met is one
   * decision, and the backend refuses the transition without one.
   */
  changeRequirementStatus: (
    id: Uuid,
    action: RequirementAction,
    body: {
      reason?: string;
      version?: number;
      evidence_document_id?: Uuid;
    } = {},
  ) =>
    http.post<ComplianceRequirement>(`/compliance/requirements/${id}/${action}`, body),

  /** The tiles above the requirement list: `region_id` narrows them alike (#468). */
  summary: (params: { region_id?: Uuid } = {}) =>
    http.get<{ by_status: Record<string, number>; total: number }>(
      "/compliance/summary",
      params,
    ),
};

/**
 * Inspection templates (LV §43-44).
 *
 * The checklist screen cannot render without one: the template supplies the questions,
 * the input types and the limits. Fetched separately from the inspection because the
 * same template serves every inspection of that category and is worth caching.
 */
export const inspectionTemplateService = {
  list: (params: { asset_category_id?: Uuid; inspection_type?: string } = {}) =>
    http.get<InspectionTemplate[]>("/inspections/templates", params),

  get: (id: Uuid) => http.get<InspectionTemplate>(`/inspections/templates/${id}`),
};

function queryOf(params: Record<string, string | undefined>): string {
  return buildReportQuery(params);
}

function buildReportQuery(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

/**
 * The server-supplied filename, preferring the RFC 5987 form.
 *
 * `filename*=UTF-8''St%C3%B6rungsbericht.csv` is the one that survives an umlaut;
 * the plain `filename=` beside it is an ASCII fallback for old clients, and taking
 * that one would save the file as `St?rungsbericht.csv`.
 */
function filenameFrom(response: Response): string | null {
  const header = response.headers.get("content-disposition");
  if (!header) return null;

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // A malformed header must not stop the download; fall through to the plain form.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

// ---------------------------------------------------------------- contractors (LV §62)
export const contractorService = {
  list: (
    params: ListParams & {
      search?: string;
      approved?: boolean;
      active?: boolean;
    } = {},
  ) => http.get<Page<Contractor>>("/contractors", params),

  get: (id: Uuid) => http.get<Contractor>(`/contractors/${id}`),

  create: (body: {
    vendor_number: string;
    company_name: string;
    contact_name?: string | null;
    email?: string | null;
    phone?: string | null;
    framework_contract_reference?: string | null;
  }) => http.post<Contractor>("/contractors", body),

  update: (id: Uuid, body: Record<string, unknown> & { version: number }) =>
    http.patch<Contractor>(`/contractors/${id}`, body),

  remove: (id: Uuid, reason?: string) =>
    http.delete<void>(`/contractors/${id}`, { reason }),

  restore: (id: Uuid, reason: string) =>
    http.post<Contractor>(`/contractors/${id}/restore`, { reason }),

  // Approval is an action, never `PATCH approved` (LV §62, §110).
  approve: (id: Uuid, body: { version?: number; reason?: string } = {}) =>
    http.post<Contractor>(`/contractors/${id}/approve`, body),

  revokeApproval: (id: Uuid, reason: string, version?: number) =>
    http.post<Contractor>(`/contractors/${id}/revoke-approval`, { reason, version }),

  qualifications: (id: Uuid) =>
    http.get<ContractorQualification[]>(`/contractors/${id}/qualifications`),

  addQualification: (id: Uuid, body: Record<string, unknown>) =>
    http.post<ContractorQualification>(`/contractors/${id}/qualifications`, body),

  updateQualification: (
    id: Uuid,
    grantId: Uuid,
    body: Record<string, unknown> & { version: number },
  ) =>
    http.patch<ContractorQualification>(
      `/contractors/${id}/qualifications/${grantId}`,
      body,
    ),

  verifyQualification: (id: Uuid, grantId: Uuid, version?: number) =>
    http.post<ContractorQualification>(
      `/contractors/${id}/qualifications/${grantId}/verify`,
      { version },
    ),

  revokeQualificationVerification: (
    id: Uuid,
    grantId: Uuid,
    reason: string,
    version?: number,
  ) =>
    http.post<ContractorQualification>(
      `/contractors/${id}/qualifications/${grantId}/revoke-verification`,
      { reason, version },
    ),

  // The reason is not optional: the route's body is `DeleteWithReasonRequest` (#316).
  removeQualification: (id: Uuid, grantId: Uuid, reason: string) =>
    http.delete<void>(`/contractors/${id}/qualifications/${grantId}`, { reason }),

  /** LV §24's warning bands across all firms, soonest first. */
  expiringQualifications: (withinDays = 90) =>
    http.get<ContractorQualificationExpiry[]>("/contractors/qualifications/expiring", {
      within_days: withinDays,
    }),

  /**
   * The LV §25 gate, asked about a firm. Resolves with the assessment, or rejects
   * with 422 QUALIFICATION_REQUIRED / CONTRACTOR_NOT_APPROVED — whose `details`
   * carry `summary` and every unmet requirement. Do not re-derive the verdict from
   * the dates in the client (LV §134).
   */
  checkQualifications: (
    id: Uuid,
    body: { work_order_id?: Uuid; qualification_ids?: Uuid[]; as_of?: DateOnly } = {},
  ) =>
    http.post<ContractorQualificationCheck>(
      `/contractors/${id}/qualification-check`,
      body,
    ),
};
